import React, { useEffect, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '@medflex/components/stories/Modal';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Logger from '@medflex/utils/Logger';
import InputField from '@medflex/components/stories/InputField';
import Button from '@medflex/components/stories/Button';
import DropDown from '@medflex/components/stories/DropDown';
import Spinner from '@medflex/components/stories/Spinner';
import { showSuccessToast, showAlertToast } from '@medflex/components/stories/Toast';
import { trackEvent } from '@medflex/utils/Tracking';
import useUserProfile from '../hooks/useUserProfile';
import useCountries from '../hooks/useCountries';
import useModal from '../hooks/useModal';
import { MODALS } from '../hooks/useIsModal';

const validationSchema = yup.object().shape({
  address: yup.string(),
  address2: yup.string(),
  postalCode: yup.string(),
  city: yup.string(),
});

const RulerOption = () => <option disabled>-------</option>;
const NoSelectionOption = ({ children }) => {
  const { t } = useTranslation('physician');
  return <option value="none">{children || t('AddressEditModal.form.no-selection')}</option>;
};

NoSelectionOption.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

const AddressEditModal = () => {
  const { close } = useModal();
  const dispatch = useDispatch();
  const { userProfile, updatePracticeAddress, isUpdating } = useUserProfile();
  const {
    register: registerFormElement,
    handleSubmit: wrapSubmit,
    formState,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });
  const { t } = useTranslation('physician');
  const userProfileErrors = useSelector(e => e.errors.userProfile);
  const countries = useCountries();
  const { address, address2, postalCode, city, countryCode } = userProfile.attributes;

  const showGenericError = () => {
    showAlertToast(t('AddressEditModal.unexpected_error.title'));
  };

  const isSubmitDisabled = !formState.isValid || isUpdating;

  useEffect(() => {
    if (userProfileErrors) {
      Logger.error(`AddressEditModal: ${JSON.stringify(userProfileErrors)}`);
      showGenericError();
      dispatch.errors.clear();
    }
  }, [userProfileErrors]);

  const handleSubmit = async (formData, event) => {
    const netData = Object.entries(formData).reduce((memo, [key, value]) => {
      if (value !== 'none') return { ...memo, [key]: value };
      return { ...memo, [key]: null };
    }, {});

    try {
      await updatePracticeAddress(netData);
      trackEvent(event, { actionText: 'edit' });
      close({ modal: MODALS.address });
      showSuccessToast(t('AddressEditModal.success.title'));
    } catch (e) {
      e.message = `AddressEditModal#handleSubmit(): ${e.message}`;
      Logger.error(e);
      showGenericError();
    }
  };

  const handleClose = e => {
    e.preventDefault();
    close({ modal: MODALS.address });
  };

  const rearrangedCountryList = useMemo(() => {
    const { DE, AT, CH, ...restOfTheWorld } = countries;
    return {
      DE,
      AT,
      CH,
      ...restOfTheWorld,
    };
  }, [countries]);

  return (
    <Modal
      showCloseButton
      className="min-h-0 flex flex-col"
      onClickOutside={handleClose}
      onClickCloseButton={handleClose}
      title={t('AddressEditModal.title')}
    >
      <div data-component="AddressEditModal">
        <form
          onSubmit={wrapSubmit(handleSubmit)}
          className="flex flex-row flex-wrap items-start lg:justify-between"
        >
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <label className="font-semibold block" htmlFor="address">
                {t('AddressEditModal.form.address')}
              </label>
              <InputField
                ref={registerFormElement}
                name="address"
                autoComplete="street-address"
                defaultValue={address}
              />
            </div>
            <div className="space-y-2 col-span-1">
              <label className="font-semibold block" htmlFor="address2">
                {t('AddressEditModal.form.address2')}
              </label>
              <InputField
                ref={registerFormElement}
                name="address2"
                autoComplete="organization"
                defaultValue={address2}
              />
            </div>
            <div className="space-y-2 col-span-1">
              <label className="font-semibold block" htmlFor="postalCode">
                {t('AddressEditModal.form.postalCode')}
              </label>
              <InputField
                ref={registerFormElement}
                name="postalCode"
                autoComplete="postal-code"
                defaultValue={postalCode}
              />
            </div>
            <div className="space-y-2 col-span-1">
              <label className="font-semibold block" htmlFor="city">
                {t('AddressEditModal.form.city')}
              </label>
              <InputField
                ref={registerFormElement}
                name="city"
                autoComplete="address-level2"
                defaultValue={city}
              />
            </div>
            <div className="space-y-2 col-span-1">
              <label className="font-semibold block" htmlFor="country">
                {t('AddressEditModal.form.country')}
              </label>
              <DropDown
                name="countryCode"
                ref={registerFormElement}
                autoComplete="country"
                defaultValue={countryCode}
              >
                <NoSelectionOption />
                <RulerOption />
                {Object.entries(rearrangedCountryList).map(([key, value]) => (
                  <Fragment key={`country#${key}`}>
                    <option value={key} disabled={key === 'ruler'}>
                      {value}
                    </option>
                    {key === 'CH' && <RulerOption />}
                  </Fragment>
                ))}
              </DropDown>
            </div>
          </div>
          <div className="mt-6 flex w-full justify-end">
            <Button type="submit" isDisabled={isSubmitDisabled}>
              {isUpdating ? <Spinner /> : t('AddressEditModal.form.save')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddressEditModal;
